// @givmo/board — the module's sidebar declaration (CBG-MOD1; D-CBG-9, 2026-09-28, amended 2026-10-15
// and 2026-10-22).
//
// ONE flat list in order of use, and a single ADMIN group (CBG-UX8-MENU; D-CBG-9 amended, Jonathan
// 2026-10-22 21:10 PDT: "the Eevery Year heading doesn't make sense to me, just make it flat except the
// Admin group"; the frames approved 23:09 PDT, D-CBG-35): Meetings · Written consents · Minute book ·
// Board members · Committees · Governance · Conflicts of interest · Self-assessment · Form 990 · Board
// Asks (Email Campaign plan §2, step 6: "a Board Asks page in the Board module"), then
// the console's own Team · Profile · Settings under the one caption the module names (`adminCaption`,
// Org Machinery OM-69: the shell's Team row opens the Organization section, which draws it). The
// Decisions, Records and People headings (2026-10-15) retired with it, and no other heading is added.
// The order, from the approved design (design.md §1): how the board decides, at a meeting and
// without one; where both land once approved; who serves; the rules they serve under; then the year's
// three, in the order the year runs them, the Form 990 last because it reports on the others.
//
// Its one section draws no caption (OM-27: one section), and its label is the list's accessible name,
// so a screen reader hears "Board", then "Admin". "Evaluations" shows as "Self-assessment" (D-CBG-35
// (1)); its address and id keep the page's first word. Form 990 is the one row whose page an
// organization type may lack: the read refuses a type with no Form 990 page (a typed 409), and the
// module names no type, so a console whose organizations file no Form 990 leaves the row out when it
// mounts Board. The import workspace is an action inside the Minute book and the calendar settings are
// the Board section of the console's Settings page, so neither is a row here. The import workspace's
// pages and a legal hold's report keep the Minute book row lit (`alsoActiveUnder`), so every Board
// page lights exactly one row.
//
// Every row requires the ONE string its page's list read names — `board:read` for every one — so a
// person without a Board grant meets a request row, never a live link into a refused read.
//
// The row ids are the console's `menu-<id>` testid contract and are prefixed with the slug:
// a borrowed row keeps its borrower's id, so an unprefixed `meetings` could meet another module's.
// They are unchanged by the flat list: only the order, the one label and the caption moved.
//
// CBG-AI5-PROPOSE: two counts the module supplies (Org Machinery OM-30's `countBadge`; the shell
// draws its one "N waiting" chip, and nothing while zero or unknown): beside Board members, the
// proposed members waiting to be added or discarded; beside Minute book, the past records waiting
// for a person to confirm them, summed over their sets (cut inbox's review fold, M9). Each is read only for someone holding that act's string
// (`features/proposals/proposal-queries.ts`).
//
// CBG-YR4-ASSESS-DARK: one row the module may withhold (Org Machinery OM-99's `served`): the
// Self-assessment row is drawn only once the backend serves the self-assessment, which it does in no
// environment until a customer asks for it (Jonathan, 2026-10-24) — the list then reads without it.

import type { LucideLikeIcon } from '@givmo/design-system';
import type { ModuleNav } from '@givmo/org-console';
import {
  BookText,
  CalendarDays,
  ClipboardCheck,
  FileSignature,
  FileText,
  Handshake,
  Network,
  Scale,
  Megaphone,
  Users,
} from 'lucide-react';
import {
  BOARD_LEGAL_HOLDS,
  BOARD_PAGES,
  BOARD_READ_PERMISSION,
  BOARD_SLUG,
  boardConsolePath,
} from './board-routes';
import { useBoardAsksServed } from './features/asks/asks-queries';
import { useSelfAssessmentServed } from './features/evaluations/assessment-queries';
import { useWaitingBatchCount, useWaitingMemberCount } from './features/proposals/proposal-queries';

/** The module's own pages, the shell supplying Team, the frame and the Organization section. */
export const BOARD_NAV: ModuleNav<LucideLikeIcon> = {
  slug: BOARD_SLUG,
  sections: [
    {
      id: BOARD_SLUG,
      // Never drawn (one section): the list's accessible name, the module's own.
      label: 'Board',
      rows: [
        {
          id: 'board-meetings',
          label: 'Meetings',
          icon: CalendarDays,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.meetings) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-consents',
          label: 'Written consents',
          icon: FileSignature,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.consents) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-minute-book',
          label: 'Minute book',
          icon: BookText,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.minuteBook) },
          requires: BOARD_READ_PERMISSION,
          // CBG-AI5-PROPOSE: the batches of past records waiting for a confirmation.
          countBadge: { useCount: useWaitingBatchCount },
          // Pages the Minute book owns beneath no address of its own keep this row lit, as a
          // meeting's pages keep Meetings lit (D-CBG-9): the import workspace, its action (the
          // batches and each batch), and a legal hold's report, the minute-book records a hold
          // covers (which keeps its address since D-CBG-53 took the holds off Settings).
          alsoActiveUnder: [
            boardConsolePath(BOARD_PAGES.importBatches),
            boardConsolePath(BOARD_LEGAL_HOLDS),
          ],
        },
        {
          id: 'board-members',
          label: 'Board members',
          icon: Users,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.members) },
          requires: BOARD_READ_PERMISSION,
          // CBG-AI5-PROPOSE: the proposed members waiting to be added or discarded.
          countBadge: { useCount: useWaitingMemberCount },
        },
        {
          id: 'board-committees',
          label: 'Committees',
          icon: Network,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.committees) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-governance',
          label: 'Governance',
          icon: Scale,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.governance) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-conflicts',
          label: 'Conflicts of interest',
          icon: Handshake,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.conflicts) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-evaluations',
          // "Evaluations" shows as "Self-assessment" (D-CBG-35 (1); its word approved in the wayfinding
          // copy round, 2026-10-24), the page's own title too (`assessment-words.ts`).
          label: 'Self-assessment',
          icon: ClipboardCheck,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.evaluations) },
          requires: BOARD_READ_PERMISSION,
          // CBG-YR4-ASSESS-DARK: drawn only once the backend serves the self-assessment (Org
          // Machinery OM-99): while it is dark its list read is the route miss and the row is absent.
          served: { useServed: useSelfAssessmentServed },
        },
        {
          id: 'board-form-990',
          label: 'Form 990',
          icon: FileText,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.form990) },
          requires: BOARD_READ_PERMISSION,
        },
        {
          id: 'board-asks',
          label: 'Board Asks',
          icon: Megaphone,
          destination: { kind: 'page', path: boardConsolePath(BOARD_PAGES.asks) },
          requires: BOARD_READ_PERMISSION,
          // EMC-WEB4: drawn only once the backend serves the Board Ask: while it is dark (or dark
          // for the organization) its list read is the route miss and the row is absent.
          served: { useServed: useBoardAsksServed },
        },
      ],
    },
  ],
  includeOrganizationSection: true,
  // The one caption, drawn "ADMIN" (the caption style), over Team · Profile · Settings (D-CBG-9
  // amended 2026-10-22; design.md §9; its word approved in the wayfinding copy round, 2026-10-24).
  adminCaption: 'Admin',
};
